// Support card: works out from the opening hours whether the team is online and,
// if not, when they are back. Hours are read in the store's time zone (Dubai); the
// "back at" time is shown in the visitor's own time zone, with its short name when
// that differs from the store's.
class SupportCard extends HTMLElement {
  connectedCallback() {
    try {
      this.hours = JSON.parse(this.dataset.hours || '{}');
    } catch {
      this.hours = {};
    }
    this.timezone = this.dataset.timezone || 'Asia/Dubai';
    this.locale = document.documentElement.lang || undefined;
    this.statusEl = this.querySelector('[data-support-status]');
    this.messageEl = this.querySelector('[data-support-message]');
    this.render();
    this.timer = setInterval(() => this.render(), 60000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  // "10:30-23:30" -> [630, 1410] minutes after midnight, or null when closed that day.
  range(day) {
    const match = /^\s*(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})\s*$/.exec(this.hours[day] || '');
    if (!match) return null;
    return [Number(match[1]) * 60 + Number(match[2]), Number(match[3]) * 60 + Number(match[4])];
  }

  now() {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: this.timezone, weekday: 'short', year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = (type) => parts.find((p) => p.type === type)?.value;
    const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
    return {
      day,
      minutes: Number(get('hour')) * 60 + Number(get('minute')),
      year: Number(get('year')), month: Number(get('month')), date: Number(get('day')),
    };
  }

  // How far the store's time zone is ahead of UTC at a given moment, in ms (handles daylight saving).
  offsetAt(ms) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: this.timezone, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23',
    }).formatToParts(new Date(ms));
    const get = (type) => Number(parts.find((p) => p.type === type)?.value);
    return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second')) - Math.floor(ms / 1000) * 1000;
  }

  // The real moment the store's wall-clock time (on the given store date) happens.
  instantFor(year, month, date, minutes) {
    const wall = Date.UTC(year, month - 1, date, 0, minutes);
    let instant = wall - this.offsetAt(wall);
    instant = wall - this.offsetAt(instant);
    return instant;
  }

  formatTime(minutes) {
    const date = new Date(Date.UTC(2000, 0, 1, Math.floor(minutes / 60), minutes % 60));
    return new Intl.DateTimeFormat(this.locale, { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }).format(date);
  }

  dayName(offset, fromDay) {
    // Any date that falls on the wanted weekday works for naming it.
    const date = new Date(Date.UTC(2000, 0, 2 + ((fromDay + offset) % 7)));
    return new Intl.DateTimeFormat(this.locale, { weekday: 'long', timeZone: 'UTC' }).format(date);
  }

  nextOpening(now) {
    const today = this.range(now.day);
    if (today && now.minutes < today[0]) return { offset: 0, start: today[0] };
    for (let offset = 1; offset <= 7; offset += 1) {
      const range = this.range((now.day + offset) % 7);
      if (range) return { offset, start: range[0] };
    }
    return null;
  }

  render() {
    const now = this.now();
    const today = this.range(now.day);
    const open = !!today && now.minutes >= today[0] && now.minutes < today[1];
    this.classList.toggle('is-online', open);
    let status = this.dataset.closedText;
    if (open) {
      status = this.dataset.onlineText;
    } else {
      const next = this.nextOpening(now);
      if (next) {
        const instant = this.instantFor(now.year, now.month, now.date + next.offset, next.start);
        const visitorZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timeFormat = new Intl.DateTimeFormat(this.locale, { hour: 'numeric', minute: '2-digit' });
        let time = timeFormat.format(instant);
        if (visitorZone && visitorZone !== this.timezone) {
          const zoneName = new Intl.DateTimeFormat(this.locale, { timeZoneName: 'short' }).formatToParts(instant).find((p) => p.type === 'timeZoneName')?.value;
          if (zoneName) time += ` (${zoneName})`;
        }
        // Day words are relative to the visitor's own calendar.
        const dayKey = (ms) => new Intl.DateTimeFormat('en-CA').format(ms);
        const diff = Math.round((Date.parse(dayKey(instant)) - Date.parse(dayKey(Date.now()))) / 86400000);
        if (diff <= 0) status = this.dataset.backToday.replace('__TIME__', time);
        else if (diff === 1) status = this.dataset.backTomorrow.replace('__TIME__', time);
        else {
          const dayLabel = new Intl.DateTimeFormat(this.locale, { weekday: 'long' }).format(instant);
          status = this.dataset.backDay.replace('__DAY__', dayLabel).replace('__TIME__', time);
        }
      }
    }
    if (this.statusEl) this.statusEl.textContent = status;
    if (this.messageEl) {
      const message = open ? this.dataset.messageOnline : this.dataset.messageOffline;
      if (message) this.messageEl.innerHTML = message;
    }
  }
}

if (!customElements.get('support-card')) {
  customElements.define('support-card', SupportCard);
}
